<template>
  <ListWrapper
    ref="listWrapper"
    :url="url"
    :hasAddButton="false"
    :addButtonText="$lang.newCenterExecute"
    :emptyImage="generateEmptyImage"
    :emptyText="generateEmptyText"
  >
    <template slot-scope="{ items }">
      <CardList
        :items="generateItems(items)"
        loadingComponent="CompleteInfo"
        accordion=""
        :hasBody="false"
      >
        <template slot="header" slot-scope="{ item }">
          <b-row
            class="margin-0 width-percent-100 font-size-12 color-complementary-300 position-relative"
          >
            <b-col
              cols="12"
              lg="4"
              class="flex-start-start flex-direction-column padding-0"
            >
              <div class="color-complementary-400 font-weight-500">
                {{ item.centerExecuteCaption }}
              </div>
              <div>
                {{ $lang.centerExecuteNumber }}:
                <CopyTextWrapper
                  :text="item.centerExecuteId"
                  textClass="color-complementary-200 cursor--pointer"
                />
              </div>
              <div>
                {{ $lang.startDate }}:
                <span>
                  {{ item.startDate }}
                </span>
              </div>
              <div class="flex-start">
                {{ $lang.centerExecuteStatus }}:
                <span
                  v-if="item.isSuspension"
                  class="chips chips--error margin-right-8"
                >
                  {{ $lang.suspension }}
                </span>
                <span
                  v-else
                  :class="`chips margin-right-8 background-${generateCenterExecuteStatusColor(
                    item.statusId
                  )}`"
                >
                  {{ item.statusCaption }}
                </span>
              </div>
            </b-col>
            <b-col cols="12" lg="8" class="padding-0">
              <b-row class="margin-0 width-percent-100">
                <b-col
                  cols="12"
                  class="border--solid border--complementary-50 border--bottom padding-0 padding-bottom-8 margin-bottom-8 font-size-12"
                >
                  <b-row class="margin-0">
                    <b-col cols="12" lg="6" class="padding-0">
                      <div
                        v-if="item.candidateFullName"
                        class="flex-start width-percent-100"
                      >
                        <span class="padding-left-4 color-complementary-400">
                          {{ item.candidateFullName }}
                        </span>
                        <span class="color-complementary-100 margin-x-4">
                          |
                        </span>
                        <span class="color-complementary-200">
                          <CopyTextWrapper
                            :text="item.candidateParvandehNumber"
                            textClass="color-complementary-200 cursor--pointer"
                          />
                        </span>
                        <span class="color-complementary-200 margin-right-4">
                          ({{ $lang.candidate }})
                        </span>
                      </div>
                      <div v-if="item.candidateFullName" class="font-size-12">
                        {{ $lang.acCode }}:
                        {{ item.candidateCode }}
                      </div>
                      <div v-if="!item.candidateFullName" class="color-warning">
                        {{ $lang.notDeterminedCandidate }}
                      </div>
                    </b-col>
                    <b-col cols="12" lg="6" class="padding-0">
                      <div class="font-size-12">
                        {{ $lang.centerExecuteCaseNumber }}:
                        <CopyTextWrapper
                          :text="item.caseNumber"
                          textClass="color-complementary-200 cursor--pointer"
                        />
                      </div>
                      <div class="font-size-12 flex-start">
                        {{ $lang.case_parvandeh_status }}:
                        <span
                          v-if="item.isAbsent"
                          class="chips chips--error-light margin-right-8"
                        >
                          {{ $lang.notPresentInExercise }}
                        </span>
                        <span
                          v-else
                          class="chips margin-right-8"
                          :class="`background-${generateCaseStatusColor(
                            item.caseStatusId
                          )}`"
                        >
                          {{ item.caseStatusCaption }}
                        </span>
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col cols="12" class="padding-0">
                  <b-row class="margin-0">
                    <b-col cols="12" lg="6" class="padding-0">
                      <div
                        v-if="item.assessorFullName"
                        class="flex-start width-percent-100"
                      >
                        <span
                          class="padding-left-4 color-complementary-400 font-weight-500"
                        >
                          {{
                            item.assessorFullName || $lang.notDeterminedAssessor
                          }}
                        </span>
                        <span class="color-complementary-100 margin-x-4">
                          |
                        </span>
                        <span class="color-complementary-200">
                          <CopyTextWrapper
                            :text="item.assessorParvandehNumber"
                            textClass="color-complementary-200 cursor--pointer"
                          />
                        </span>
                        <span class="color-complementary-200 margin-right-4">
                          ({{ $lang.assessor }})
                        </span>
                      </div>
                      <div v-if="item.assessorFullName">
                        {{ $lang.acCode }}:
                        {{ item.assessorCode }}
                      </div>
                      <div v-if="!item.assessorFullName" class="color-warning">
                        {{ $lang.notDeterminedAssessor }}
                      </div>
                    </b-col>
                    <b-col cols="12" lg="6" class="padding-0">
                      <div class="flex-start width-percent-100">
                        <span
                          class="padding-left-4 color-complementary-400 font-weight-500"
                        >
                          {{ item.exerciseName }}
                        </span>
                        <span class="color-complementary-100 margin-x-4">
                          |
                        </span>
                        <span class="color-complementary-200">
                          {{ item.exerciseNumber }}
                        </span>
                      </div>
                      <div>
                        {{ $lang.evidenceSelected }}:
                        {{ item.hasEvidence ? $lang.has : $lang.doesNotHave }}
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </template>
      </CardList>
    </template>
  </ListWrapper>
</template>

<script>
import { ListWrapper, CardList, CopyTextWrapper } from "@/components/re-design";
import { computed } from "vue";
import { pathOr } from "ramda";
import { generateApiUrl, generateConfig, generateLang } from "@/shared/utils";

export default {
  components: {
    ListWrapper,
    CardList,
    CopyTextWrapper,
  },
  setup() {
    const url = computed(() => {
      return generateApiUrl(["kanoon", "centerExecuteCompleteInfo"]);
    });
    const generateEmptyImage = computed(() => {
      return generateConfig(["images", "shared", "redesign", "empty", "list"]);
    });
    const generateEmptyText = computed(() => {
      return generateLang(["list_empty_text"]);
    });

    const generateItems = (items) => {
      return items.map((item) => {
        return {
          centerExecuteCaption: pathOr("", ["center_execute_caption"], item),
          centerExecuteId: pathOr("", ["center_execute_id"], item),
          startDate: pathOr("", ["start_date", "jalali"], item),
          statusId: pathOr("", ["status", "id"], item),
          statusCaption: pathOr("", ["status", "caption"], item),
          candidateFullName: pathOr("", ["candidate", "full_name"], item),
          candidateParvandehNumber: pathOr(
            "",
            ["candidate", "parvandeh_number"],
            item
          ),
          candidateCode: pathOr("", ["candidate", "code"], item),
          caseNumber: pathOr("", ["case_number"], item),
          caseStatusId: pathOr("", ["case_status", "id"], item),
          caseStatusCaption: pathOr("", ["case_status", "caption"], item),
          assessorFullName: pathOr("", ["assessor", "full_name"], item),
          assessorParvandehNumber: pathOr(
            "",
            ["assessor", "parvandeh_number"],
            item
          ),
          assessorCode: pathOr("", ["assessor", "code"], item),
          exerciseName: pathOr("", ["exercise_name"], item),
          exerciseNumber: pathOr("", ["exercise_number"], item),
          hasEvidence: pathOr(false, ["has_evidence"], item),
          isAbsent: !!pathOr(0, ["candidate", "is_absent"], item),
          isSuspension: !!pathOr(null, ["incomplete_completion_user_id"], item),
        };
      });
    };

    const generateCenterExecuteStatusColor = (id = 1) => {
      const colors = {
        1: "warning-50 color-warning",
        2: "warning-50 color-warning",
        3: "success-50 color-success",
        4: "warning-50 color-warning",
        5: "success-50 color-success",
      };
      return pathOr("", [id], colors);
    };
    const generateCaseStatusColor = (id = 1) => {
      const colors = {
        1: "warning-50 color-warning",
        2: "primary-50 color-primary",
        3: "success-50 color-success",
        4: "success color-surface",
        5: "complementary-50 color-complementary-200",
        6: "complementary-50 color-complementary-200",
        7: "warning color-surface",
        8: "primary color-surface",
      };
      return pathOr("", [id], colors);
    };

    // return data to be used in template
    return {
      url,
      generateEmptyImage,
      generateEmptyText,
      generateItems,
      generateCenterExecuteStatusColor,
      generateCaseStatusColor,
    };
  },
};
</script>
